/**
 * CollabCore CRUD 组合式函数（对标 cool-admin useCrud）
 */

import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import { dict } from '../config'
import { usePermission } from '@/composables/usePermission'
import type { Permission } from '@/utils/permission'
import type { CollabPermissionConfig, CollabService } from '../types/crud'
import type { PageQuery } from '../types/contract'

export interface UseCollabCrudOptions<T = Record<string, unknown>> {
  /** CollabCore 六接口服务 */
  service: CollabService<T>
  /** 按钮权限配置 */
  permission?: CollabPermissionConfig
  /** 默认每页条数 */
  defaultPageSize?: number
  /** 新增表单初始值 */
  createDefaults?: Record<string, unknown>
}

export function useCollabCrud<T = Record<string, unknown>>(
  options: UseCollabCrudOptions<T>,
) {
  const { service, permission, defaultPageSize = 20, createDefaults = {} } = options
  const { hasPermission } = usePermission()
  const { t } = useI18n()

  const loading = ref(false)
  const formLoading = ref(false)
  const dataSource = ref<T[]>([])
  const total = ref(0)
  const selectedRows = ref<T[]>([])
  const formVisible = ref(false)
  const formMode = ref<'add' | 'edit'>('add')
  const formModel = ref<Record<string, unknown>>({})

  // 当前查询参数（分页、搜索、排序）
  const queryParams = reactive<PageQuery>({
    page: 1,
    size: defaultPageSize,
    keyWord: undefined,
    order: undefined,
    sort: undefined,
  })

  const pagination = computed(() => ({
    current: queryParams.page ?? 1,
    pageSize: queryParams.size ?? defaultPageSize,
    total: total.value,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (count: number) => `共 ${count} 条记录`,
  }))

  const canAdd = computed(
    () => !permission?.add || hasPermission(permission.add as Permission),
  )
  const canUpdate = computed(
    () => !permission?.update || hasPermission(permission.update as Permission),
  )
  const canDelete = computed(
    () => !permission?.delete || hasPermission(permission.delete as Permission),
  )

  /** 返回当前查询参数快照 */
  function getParams(): PageQuery {
    return { ...queryParams }
  }

  /** 刷新列表（默认调 page），可合并额外查询参数 */
  async function refresh(params?: Partial<PageQuery>) {
    if (params) {
      Object.assign(queryParams, params)
    }
    loading.value = true
    try {
      const result = await service.page(getParams())
      dataSource.value = result.list ?? []
      total.value = result.pagination?.total ?? 0
      if (result.pagination) {
        queryParams.page = result.pagination.page
        queryParams.size = result.pagination.size
      }
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  /** 打开新增表单 */
  function rowAdd() {
    formMode.value = 'add'
    formModel.value = { ...createDefaults }
    formVisible.value = true
  }

  /** 打开编辑表单 */
  function rowEdit(row: Record<string, unknown>) {
    formMode.value = 'edit'
    formModel.value = { ...row }
    formVisible.value = true
  }

  /** 关闭表单弹窗 */
  function rowClose() {
    formVisible.value = false
    formModel.value = {}
  }

  /** 删除选中行（单条或批量） */
  async function rowDelete(selection: Array<Record<string, unknown>>) {
    const primaryKey = dict.primaryId
    const ids = selection
      .map((row) => row[primaryKey])
      .filter((id): id is number => typeof id === 'number')
    if (!ids.length) {
      message.warning(t('common.selectRecordsToDelete'))
      return
    }
    loading.value = true
    try {
      await service.delete({ ids })
      message.success(t('common.deleteSuccess'))
      selectedRows.value = []
      await refresh()
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  /** 提交表单（新增/编辑） */
  async function submitForm(values: Record<string, unknown>) {
    formLoading.value = true
    try {
      if (formMode.value === 'add') {
        await service.add(values as Partial<T>)
        message.success(t('common.addSuccess'))
      } else {
        await service.update({ ...formModel.value, ...values } as Partial<T> & { id: number })
        message.success(t('common.updateSuccess'))
      }
      rowClose()
      await refresh()
    } catch (error) {
      throw error
    } finally {
      formLoading.value = false
    }
  }

  /** 处理表格分页/排序变化 */
  function handleTableChange(paginationInfo: { current?: number; pageSize?: number }, _filters: unknown, sorter: { field?: string; order?: string }) {
    if (paginationInfo.current) {
      queryParams.page = paginationInfo.current
    }
    if (paginationInfo.pageSize) {
      queryParams.size = paginationInfo.pageSize
    }
    // 排序字段映射：order + sort(asc/desc)
    if (sorter?.field) {
      queryParams[dict.sort.field] = String(sorter.field)
      queryParams[dict.sort.direction] =
        sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined
    }
    return refresh()
  }

  /** 关键字搜索（重置到第一页） */
  function handleSearch(keyWord?: string) {
    return refresh({ keyWord: keyWord || undefined, page: 1 })
  }

  return {
    loading,
    formLoading,
    dataSource,
    total,
    pagination,
    queryParams,
    selectedRows,
    formVisible,
    formMode,
    formModel,
    canAdd,
    canUpdate,
    canDelete,
    getParams,
    refresh,
    rowAdd,
    rowEdit,
    rowClose,
    rowDelete,
    submitForm,
    handleTableChange,
    handleSearch,
  }
}

export default useCollabCrud
